import {
  useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult,
} from 'react-query';
import httpClient from '../../core/httpClient';
import { DealStageHistory, UpdateDealStageHistoryRequest } from '../../types/deals/deals';
import { QUERY_OPTIONS } from '../../constants';

// GET /deals/{deal_id}/history
export const useGetDealStageHistoryQuery = (
  dealId: number,
): UseQueryResult<DealStageHistory[], Error> => useQuery<DealStageHistory[], Error>(
  ['dealStageHistory', dealId],
  async () => {
    const response = await httpClient.get(`/deals/${dealId}/history`);
    return response.data.items || response.data;
  },
  QUERY_OPTIONS,
);

// PUT /deals/{deal_id}/history/{history_id}
export const useUpdateDealStageHistoryMutation = (): UseMutationResult<
DealStageHistory,
Error,
{ dealId: number; historyId: number; data: UpdateDealStageHistoryRequest }
> => {
  const queryClient = useQueryClient();

  return useMutation<
  DealStageHistory,
  Error,
  { dealId: number; historyId: number; data: UpdateDealStageHistoryRequest }
  >(
    async ({ dealId, historyId, data }) => {
      const response = await httpClient.put(`/deals/${dealId}/history/${historyId}`, data);
      return response.data;
    },
    {
      onSuccess: async (data, variables) => {
        // Invalidate the deal stage history query
        await queryClient.invalidateQueries(['dealStageHistory', variables.dealId]);
        // Also invalidate the main deal query to refresh history in view page
        await queryClient.invalidateQueries(['deals', variables.dealId]);
      },
    },
  );
};

// DELETE /deals/{deal_id}/history/{history_id}
export const useDeleteDealStageHistoryMutation = (): UseMutationResult<
void,
Error,
{ dealId: number; historyId: number }
> => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { dealId: number; historyId: number }>(
    async ({ dealId, historyId }) => {
      await httpClient.delete(`/deals/${dealId}/history/${historyId}`);
    },
    {
      onSuccess: async (data, variables) => {
        // Invalidate the deal stage history query
        await queryClient.invalidateQueries(['dealStageHistory', variables.dealId]);
        // Also invalidate the main deal query to refresh history in view page
        await queryClient.invalidateQueries(['deals', variables.dealId]);
      },
    },
  );
};

// GET /deals/{deal_id}/history/{history_id}
export const useGetSingleDealStageHistoryQuery = (
  dealId: number,
  historyId: number,
): UseQueryResult<DealStageHistory, Error> => useQuery<DealStageHistory, Error>(
  ['dealStageHistory', dealId, historyId],
  async () => {
    const response = await httpClient.get(`/deals/${dealId}/history/${historyId}`);
    return response.data;
  },
  QUERY_OPTIONS,
);
